import React, {
  ChangeEvent, FocusEvent, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState
} from 'react';
import classNames from 'classnames';
import { Input, InputWrapper, IconContainer } from './styles';
import { UDCommonInputProps } from './types';
import UDInputNumberControls from './number-controls';
import UDColorableIcon from 'modules/ud-ui/components/icon/colorable-icon';
import { getMaskText } from '../helpers/getMaskText';

export type UDInputProps = UDCommonInputProps & {
  mask?: string;
  formatter?: (value: string) => string;
};

const UDInput = React.forwardRef(
  (props: UDInputProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
      hasError,
      valid,
      className,
      wrapperProps,
      iconProps,
      placeholder,
      mask = '',
      formatter,
      onChange,
      onFocus,
      onBlur,
      ...otherProps
    } = props;

    const showMaskInput = Boolean(mask);
    const commonInputClassNames = [
      'input',
      'text subhead',
      props.className,
      { '--error': hasError },
      iconProps?.position || (iconProps && 'icon-right'),
    ];

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);

    const [inputValue, setInputValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);
    const maskTextVisible = showMaskInput && (isFocused || !!inputValue);

    const inputClassName = classNames(...commonInputClassNames);
    const maskInputClassName = classNames(
      ...commonInputClassNames,
      'input-mask',
      { 'input-mask__visible': maskTextVisible },
    );

    useLayoutEffect(() => {
      const value = String(otherProps.value || '');
      const formattedValue = formatter ? formatter(value) : value;
      setInputValue(formattedValue);
    }, [formatter, otherProps.value]);


    // autofocus:
    useEffect(() => {
      if (inputRef.current === document.activeElement) {
        setIsFocused(true);
      }
    }, [inputRef]);

    const handleFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);

      if (onFocus) {
        onFocus(event);
      }
    }, [onFocus]);

    const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);

      if (onBlur) {
        onBlur(event);
      }
    }, [onBlur]);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const formattedValue = formatter ? formatter(value) : value;

      setInputValue(formattedValue);

      if (inputRef.current && value !== formattedValue) {
        inputRef.current.value = formattedValue;
      }

      if (onChange) {
        onChange(event);
      }
    }, [onChange, formatter]);

    return (
      <InputWrapper
        {...wrapperProps}
        className={classNames(wrapperProps?.className, {
          error: hasError,
          valid,
        })}>

        {showMaskInput && (
          <Input
            className={maskInputClassName}
            value={getMaskText(inputValue, mask)}
            readOnly={true}
            tabIndex={-1}
            aria-hidden={true}
          />
        )}

        <Input
          {...otherProps}
          placeholder={maskTextVisible ? undefined : placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={inputClassName}
          ref={inputRef}
        />
        {props.type === 'number' && (
          <UDInputNumberControls inputRef={inputRef} />
        )}
        {iconProps && (
          <IconContainer className={iconProps.position || 'icon-right'}>
            <UDColorableIcon
              name={iconProps.name}
              size={iconProps.size}
              componentProps={iconProps.componentProps}
            />
          </IconContainer>
        )}
      </InputWrapper>
    );
  },
);

export default UDInput;
